import { Entity } from '../entity';

export interface TenantSecurityMethods {
    workbuddy: boolean;
    microsoft: boolean;
    google: boolean;
    passkey: boolean;
    email: boolean;
}

// Advanced enterprise SSO override: a tenant brings their own Entra Enterprise App.
// clientId = their app registration; discoveryEndpoint = the full .well-known OIDC URL they paste from Entra.
// Absent for standard tenants — they fall back to the shared WorkBuddy app + common authority.
export interface SsoProviderConfig {
    clientId: string | null;
    discoveryEndpoint: string | null; // full .well-known OIDC URL; authority is derived from this server-side
}

export interface TenantSsoConfig {
    microsoft?: SsoProviderConfig;
}

export interface TenantSecuritySettings {
    customise: boolean;
    methods: TenantSecurityMethods;
    exclusions: Entity[];
    enforceConcurrentTenantWebSessions: boolean;
    maxConcurrentTenantWebSessions: number;
    enforceConcurrentFieldAppSessions: boolean;
    maxConcurrentFieldAppSessions: number;
    mfaRequired: boolean;
    mfaRequiredForSocialLogins: boolean;
    sso?: TenantSsoConfig;
}

export interface TenantPreventLoginSettings {
    enabled: boolean;
}

export type TenantSecurityMethodKey = keyof TenantSecurityMethods;

export function defaultTenantSecurityMethodValue() {
    return {
        workbuddy: true,
        microsoft: true,
        google: true,
        passkey: true,
        email: true
    };
}

export function defaultTenantSecurity() {
    return {
        customise: false,
        methods: null,
        exclusions: [],
        enforceConcurrentTenantWebSessions: true,
        maxConcurrentTenantWebSessions: 2,
        enforceConcurrentFieldAppSessions: false,
        maxConcurrentFieldAppSessions: 2,
        mfaRequired: false,
        mfaRequiredForSocialLogins: false,
    } as TenantSecuritySettings;
}

export function resolveTenantSecurity(value?: Partial<TenantSecuritySettings>): TenantSecuritySettings {

    const defaults = defaultTenantSecurity();
    const toSessionLimit = (sessionLimit: unknown, fallback: number) => Math.min(10, Math.max(1, Math.round(Number(sessionLimit ?? fallback))));

    const resolved: TenantSecuritySettings = {
        ...defaults,
        ...value,
        methods: value?.methods === undefined ? defaults.methods : value.methods,
        exclusions: value?.exclusions ?? defaults.exclusions,
        enforceConcurrentTenantWebSessions: value?.enforceConcurrentTenantWebSessions ?? defaults.enforceConcurrentTenantWebSessions,
        maxConcurrentTenantWebSessions: toSessionLimit(value?.maxConcurrentTenantWebSessions, defaults.maxConcurrentTenantWebSessions),
        enforceConcurrentFieldAppSessions: value?.enforceConcurrentFieldAppSessions ?? defaults.enforceConcurrentFieldAppSessions,
        maxConcurrentFieldAppSessions: toSessionLimit(value?.maxConcurrentFieldAppSessions, defaults.maxConcurrentFieldAppSessions),
    };

    // Advanced SSO override is optional — only carry it when a clientId is actually configured.
    // Standard tenants stay sso-less so they fall back to the shared app + common authority.
    const microsoftClientId = value?.sso?.microsoft?.clientId;
    if (microsoftClientId) {
        resolved.sso = { microsoft: { clientId: microsoftClientId, discoveryEndpoint: value?.sso?.microsoft?.discoveryEndpoint ?? null } };
    } else {
        delete resolved.sso;
    }

    return resolved;
}

export function defaultPreventLogin() {
    return {
        enabled: false
    } as TenantPreventLoginSettings;
}
